import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import './Mood.css';

const moods = [
    // Positive
    { name: 'Happy', emoji: '😊' },
    { name: 'Grinning', emoji: '😀' },
    { name: 'Beaming', emoji: '😁' },
    { name: 'ROFL', emoji: '🤣' },
    { name: 'Excited', emoji: '🤩' },
    { name: 'Loved', emoji: '🥰' },
    { name: 'In love', emoji: '😍' },
    { name: 'Kissing', emoji: '😘' },
    { name: 'Playful', emoji: '😜' },
    { name: 'Smirking', emoji: '😏' },
    { name: 'Hugging', emoji: '🤗' },
    { name: 'Star-struck', emoji: '🤩' },
    { name: 'Grateful', emoji: '🙏' },
    { name: 'Hopeful', emoji: '😇' },
    { name: 'Angelic', emoji: '😇' },
    { name: 'Blessed', emoji: '🙏' },
    { name: 'Partying', emoji: '🥳' },
    { name: 'Cowboy', emoji: '🤠' },

    // Neutral
    { name: 'Okay', emoji: '😐' },
    { name: 'Content', emoji: '😌' },
    { name: 'Relieved', emoji: '😌' },
    { name: 'Tired', emoji: '😴' },
    { name: 'Sleepy', emoji: '😪' },
    { name: 'Yawning', emoji: '🥱' },
    { name: 'Thinking', emoji: '🤔' },
    { name: 'Shushing', emoji: '🤫' },
    { name: 'Lying', emoji: '🤥' },
    { name: 'Expressionless', emoji: '😑' },
    { name: 'Unamused', emoji: '😒' },
    { name: 'Goofy', emoji: '🤪' },
    { name: 'Zany', emoji: '🤪' },
    { name: 'Silly', emoji: '🤪' },
    { name: 'Surprised', emoji: '😮' },
    { name: 'Astonished', emoji: '😲' },
    { name: 'Flushed', emoji: '😳' },
    { name: 'Nerd', emoji: '🤓' },
    { name: 'Monocle', emoji: '🧐' },

    // Negative
    { name: 'Sad', emoji: '😭' },
    { name: 'Crying', emoji: '😢' },
    { name: 'Pleading', emoji: '🥺' },
    { name: 'Anxious', emoji: '😟' },
    { name: 'Worried', emoji: '😟' },
    { name: 'Frowning', emoji: '☹️' },
    { name: 'Anguished', emoji: '😧' },
    { name: 'Fearful', emoji: '😨' },
    { name: 'Scared', emoji: '😱' },
    { name: 'Screaming', emoji: '😱' },
    { name: 'Angry', emoji: '😠' },
    { name: 'Annoyed', emoji: '😠' },
    { name: 'Stressed', emoji: '😫' },
    { name: 'Overwhelmed', emoji: '😫' },
    { name: 'Sick', emoji: '🤢' },
    { name: 'Vomiting', emoji: '🤮' },
    { name: 'Disappointed', emoji: '😞' },
    { name: 'Pensive', emoji: '😔' },
    { name: 'Grimacing', emoji: '😬' },
    { name: 'Hot', emoji: '🥵' },
    { name: 'Cold', emoji: '🥶' },
    { name: 'Woozy', emoji: '🥴' },
    { name: 'Mind Blown', emoji: '🤯' },


    // Other
    { name: 'Horny', emoji: '😈' },
    { name: 'Mischievous', emoji: '😈' },
    { name: 'Creative', emoji: '🎨' },
    { name: 'Inspired', emoji: '🎨' },
    { name: 'Busy', emoji: '🏃‍♀️' },
    { name: 'Productive', emoji: '🏃‍♀️' },
    { name: 'Relaxed', emoji: '🧘' },
    { name: 'Zen', emoji: '🧘' },
];

const Mood = () => {
    const [coupleId, setCoupleId] = useState(null);
    const [userMood, setUserMood] = useState(null);
    const [partnerMood, setPartnerMood] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubAuth = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setUser(userData);
                    if (userData.partnerId) {
                        const ids = [currentUser.uid, userData.partnerId].sort();
                        setCoupleId(ids.join('_'));
                    } else {
                        setLoading(false);
                    }
                }
            } else {
                setUser(null);
                setCoupleId(null);
                setLoading(false);
            }
        });
        return () => unsubAuth();
    }, []);

    useEffect(() => {
        if (!coupleId || !user) return;

        const coupleDocRef = doc(db, 'coupleData', coupleId);
        const unsubscribe = onSnapshot(coupleDocRef, (docSnap) => {
            const today = new Date().toDateString();
            const coupleData = docSnap.exists() ? docSnap.data() : {};
            const moodsData = coupleData.moods || {};
            
            const currentUser = auth.currentUser;
            const partnerId = user.partnerId;

            // Check current user's mood
            if (moodsData[currentUser.uid] && moodsData[currentUser.uid].date === today) {
                setUserMood(moodsData[currentUser.uid]);
            } else {
                setUserMood(null);
            }

            // Check partner's mood
            if (partnerId && moodsData[partnerId] && moodsData[partnerId].date === today) {
                setPartnerMood(moodsData[partnerId]);
            } else {
                setPartnerMood(null);
            }
            
            setLoading(false);
        });

        return () => unsubscribe();
    }, [coupleId, user]);

    const handleSetMood = async (mood) => {
        if (!coupleId || !auth.currentUser) return;

        const today = new Date().toDateString();
        const coupleDocRef = doc(db, 'coupleData', coupleId);
        
        const moodData = {
            mood: mood.name,
            emoji: mood.emoji,
            date: today,
        };

        await setDoc(coupleDocRef, {
            moods: {
                [auth.currentUser.uid]: moodData
            }
        }, { merge: true });
    };

    if (loading) {
        return <div className="mood-container">Loading...</div>;
    }

    if (!user) {
        return <div className="mood-container"><p>Please log in to use the mood tracker.</p></div>;
    }
    
    if (!coupleId) {
        return <div className="mood-container"><p>Please link with a partner to use the mood tracker.</p></div>;
    }

    return (
        <div className="mood-container">
            <div className="mood-display">
                <div className="mood-card your-mood">
                    <h2>Your Mood</h2>
                    {userMood ? (
                        <div className="mood-result">
                            <span className="emoji">{userMood.emoji}</span>
                            <p>{userMood.mood}</p>
                        </div>
                    ) : (
                        <p className="no-mood-text">You haven't set your mood today.</p>
                    )}
                </div>
                <div className="mood-card partner-mood">
                    <h2>Partner's Mood</h2>
                    {partnerMood ? (
                        <div className="mood-result">
                            <span className="emoji">{partnerMood.emoji}</span>
                            <p>{partnerMood.mood}</p>
                        </div>
                    ) : (
                        <p className="no-mood-text">Your partner hasn't set their mood today.</p>
                    )}
                </div>
            </div>

            <h1>How are you feeling today?</h1>
            <div className="mood-selection">
                {moods.map((mood) => (
                    <button
                        key={mood.name}
                        className={`mood-option ${userMood?.mood === mood.name ? 'selected' : ''}`}
                        onClick={() => handleSetMood(mood)}
                    >
                        <span className="mood-emoji">{mood.emoji}</span>
                        <span className="mood-name">{mood.name}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Mood;